import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import Modal from '../../../../components/shared/Modal';
import './AISuggestionPanel.scss';

// Use environment var if provided, else default to host-relative path
const API_BASE_URL = process.env.REACT_APP_AI_API_BASE_URL || '/popin/api/1.0/ai';

const fetchAISuggestions = async (objective, questionType, userPrompt, user) => {
  console.log('[AISuggestionPanel] Fetching suggestions', { objective, questionType, userPrompt });
  console.log('[AISuggestionPanel] User from Redux:', user);
  
  // Prepare headers with user context for tracking
  const headers = {
    'Content-Type': 'application/json',
  };
  
  if (user && user.id && user.email) {
    headers['X-POPin-User-ID'] = String(user.id);
    headers['X-POPin-User-Email'] = user.email;
    console.log('[AISuggestionPanel] Adding user headers:', {
      userId: user.id,
      email: user.email
    });
  } else {
    console.warn('[AISuggestionPanel] No user in Redux store! Headers will not include user info');
    console.log('[AISuggestionPanel] User object:', user);
  }
  
  console.log('[AISuggestionPanel] Request headers:', headers);
  
  const response = await fetch(`${API_BASE_URL}/survey-generation/suggest-questions`, {
    method: 'POST',
    headers: headers,
    body: JSON.stringify({
      objective: objective,
      question_type: questionType,
      user_prompt: userPrompt,
    }),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ detail: 'An unknown error occurred while fetching suggestions.' }));
    console.error('API Error:', errorData);
    throw new Error(errorData.detail || `HTTP error! status: ${response.status}`);
  }

  const data = await response.json();
  console.log('[AISuggestionPanel] API response', data);
  return data.suggestions || [];
};

export const AISuggestionPanel = ({ isOpen, onClose, surveyObjective, questionType, onSelectSuggestion }) => {
  const [suggestions, setSuggestions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [userPrompt, setUserPrompt] = useState('');
  const [error, setError] = useState('');
  
  // Get user from Redux store for tracking
  const user = useSelector((state) => state?.user?.user);

  const handleFetchSuggestions = async () => {
    if (!surveyObjective && !userPrompt) {
      setError('Please enter a prompt or survey objective.');
      return;
    }
    setIsLoading(true);
    setError('');
    setSuggestions([]);
    try {
      const result = await fetchAISuggestions(surveyObjective, questionType, userPrompt, user);
      setSuggestions(result);
    } catch (err) {
      setError(err.message || 'Failed to fetch suggestions. Please try again.');
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="AI-Powered Question Suggestions">
      <div className="ai-suggestion-panel">
        <div className="ai-suggestion-header">
          <p>Tell the AI what kind of question you’d like, then click Generate.</p>
          <textarea
            value={userPrompt}
            onChange={(e) => setUserPrompt(e.target.value)}
            placeholder="e.g. Ask about employee morale regarding remote work"
            rows={3}
          />
          <button className="btn btn-primary" onClick={handleFetchSuggestions} disabled={isLoading}>
            {isLoading ? 'Generating...' : 'Generate Suggestions'}
          </button>
        </div>

        {error && <div className="error-message">{error}</div>}

        {isLoading && (
          <div className="loading-spinner">
            <div className="spinner"></div>
            <p>Thinking...</p>
          </div>
        )}

        {!isLoading && suggestions.length > 0 && (
          <div className="suggestions-list">
            <ul>
              {suggestions.map((suggestion, index) => (
                <li key={index} onClick={() => onSelectSuggestion(suggestion)}>
                  {suggestion}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </Modal>
  );
};
